// 店舗グループ階層ツリーの構築ロジック

import type { MiseSellerGroupNode } from '@/lib/types/mise/seller';

/**
 * ツリー描画用にフラット化した1要素。
 * pre-order（親→子）で並ぶため、配列順にそのまま描画すれば階層が表現できる。
 */
export interface FlatSellerGroupTreeItem {
  node: MiseSellerGroupNode;
  /** 0始まりのネスト深さ（インデント用） */
  depth: number;
  /** 兄弟内で最後の子か（コネクタ └─ / ├─ の出し分け用） */
  isLast: boolean;
  /** 各祖先が「最後の子」だったか（縦ガイド │ の描画制御用、長さ = depth） */
  ancestorIsLast: boolean[];
}

/**
 * 兄弟ノードを sort_order 昇順 → name 昇順（null は最後）で並べる。
 * バックエンドの順序に依存せずフロント側で一意な並びを保証するため。
 */
function compareSiblings(a: MiseSellerGroupNode, b: MiseSellerGroupNode): number {
  if (a.sort_order !== b.sort_order) {
    return a.sort_order - b.sort_order;
  }
  // name が null のものは末尾に寄せる
  if (a.name === null && b.name === null) return 0;
  if (a.name === null) return 1;
  if (b.name === null) return -1;
  return a.name.localeCompare(b.name);
}

/**
 * フラットな店舗グループ一覧を pre-order でフラット化したツリー配列に変換する。
 *
 * - parent_id が null、または一覧に存在しない親を指すノードはルート扱い。
 * - 循環参照や親不在に対しても無限ループせず、全ノードを必ず1回ずつ出力する。
 */
export function buildSellerGroupTree(nodes: MiseSellerGroupNode[]): FlatSellerGroupTreeItem[] {
  // id → ノードの索引。親の存在判定に使う。
  const nodeById = new Map<string, MiseSellerGroupNode>();
  for (const node of nodes) {
    nodeById.set(node.id, node);
  }

  // 親id → 子ノード配列。ルートは null キーにまとめる。
  const childrenByParent = new Map<string | null, MiseSellerGroupNode[]>();
  for (const node of nodes) {
    // 親が一覧に存在しない（または null）ならルートとして扱う
    const parentKey =
      node.parent_id !== null && nodeById.has(node.parent_id) ? node.parent_id : null;
    const siblings = childrenByParent.get(parentKey);
    if (siblings) {
      siblings.push(node);
    } else {
      childrenByParent.set(parentKey, [node]);
    }
  }

  // 各親グループ内の兄弟を一意な順序にソート
  for (const siblings of childrenByParent.values()) {
    siblings.sort(compareSiblings);
  }

  const result: FlatSellerGroupTreeItem[] = [];
  // 循環参照があっても同一ノードを二度たどらないための訪問済み集合
  const visited = new Set<string>();

  // 兄弟集合を pre-order で result に積む。
  // isLast は兄弟配列内の位置から決まるため、ここで算出して子の祖先情報に引き継ぐ。
  const walk = (siblings: MiseSellerGroupNode[], depth: number, ancestorIsLast: boolean[]) => {
    siblings.forEach((node, index) => {
      // 既訪問なら循環とみなして打ち切る（無限ループ防止）
      if (visited.has(node.id)) return;
      visited.add(node.id);

      const isLast = index === siblings.length - 1;
      result.push({ node, depth, isLast, ancestorIsLast });

      const children = childrenByParent.get(node.id) ?? [];
      walk(children, depth + 1, [...ancestorIsLast, isLast]);
    });
  };

  walk(childrenByParent.get(null) ?? [], 0, []);

  return result;
}

/**
 * ネイティブ <option> 用に階層の深さ分インデントしたラベルを生成する。
 * option には CSS の padding が効かないため、全角スペースと枝記号で階層を表現する。
 */
export function indentSellerGroupLabel(depth: number, label: string): string {
  if (depth <= 0) return label;
  return '　'.repeat(depth - 1) + '└─ ' + label;
}

/** プルダウン用に階層順へ並べた店舗グループ（depth はインデント用） */
export interface SellerGroupOption<T> {
  group: T;
  depth: number;
}

/**
 * 店舗グループを階層ツリー順に並べ替え、各項目に深さ(depth)を付与する。
 * mise.seller_groups_* は name しか持たないため、snapshot_hot 由来の階層ツリーから順序・深さを引く。
 * ツリーに無いグループは末尾に名前順で並べる。
 */
export function sortSellerGroupsByTree<T extends { id: string; name: string | null }>(
  groups: T[],
  nodes: MiseSellerGroupNode[],
): SellerGroupOption<T>[] {
  const treeInfo = new Map<string, { order: number; depth: number }>();
  buildSellerGroupTree(nodes).forEach((item, index) => {
    treeInfo.set(item.node.id, { order: index, depth: item.depth });
  });
  return groups
    .map((group) => ({
      group,
      order: treeInfo.get(group.id)?.order ?? Number.MAX_SAFE_INTEGER,
      depth: treeInfo.get(group.id)?.depth ?? 0,
    }))
    .sort((a, b) =>
      a.order !== b.order
        ? a.order - b.order
        : (a.group.name ?? '').localeCompare(b.group.name ?? ''),
    )
    .map(({ group, depth }) => ({ group, depth }));
}

/**
 * 店舗が直接ぶら下がる店舗グループから最上位までをたどり、上の階層から順に並べて返す。
 * 一覧に無い親や循環参照に当たった時点で打ち切る。
 */
export function buildSellerGroupPath(
  parentGroupId: string | null,
  nodes: MiseSellerGroupNode[],
): MiseSellerGroupNode[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const path: MiseSellerGroupNode[] = [];
  const visited = new Set<string>();
  let currentId = parentGroupId;
  while (currentId !== null && !visited.has(currentId)) {
    const node = nodeById.get(currentId);
    if (!node) break;
    visited.add(currentId);
    path.unshift(node);
    currentId = node.parent_id;
  }
  return path;
}

/**
 * 指定した階層（1始まり）にあるグループ名を重複なしで maxExamples 件まで「、」区切りで返す。
 * 階層そのものには名前が無いため、選択肢のラベルに例として添えて階層を見分けられるようにする。
 */
export function describeLevelExamples(
  paths: MiseSellerGroupNode[][],
  level: number,
  maxExamples = 3,
): string {
  const names = new Set<string>();
  for (const path of paths) {
    const node = path[level - 1];
    if (node) names.add(node.name || node.id);
  }
  const examples = Array.from(names);
  const shown = examples.slice(0, maxExamples).join('、');
  return examples.length > maxExamples ? `${shown}…` : shown;
}

/**
 * 階層パスから指定した階層（1始まり）のグループを返す。
 * 届かない店舗はツリーの深さがエリアごとに違うだけなので、最下層をその店舗で最も細かい区分として返す。
 */
export function pickGroupAtLevel(
  path: MiseSellerGroupNode[],
  level: number,
): { node: MiseSellerGroupNode; level: number } | undefined {
  if (path.length === 0) return undefined;
  const pickedLevel = Math.min(level, path.length);
  return { node: path[pickedLevel - 1], level: pickedLevel };
}

/**
 * 階層パス同士を、ツリーを上から順に描いたときの並び（pre-order）で比べる。
 * ツリー未所属（空パス）は最後に並べる。
 */
export function compareSellerGroupPaths(
  a: MiseSellerGroupNode[],
  b: MiseSellerGroupNode[],
): number {
  if (a.length === 0 || b.length === 0) return (a.length === 0 ? 1 : 0) - (b.length === 0 ? 1 : 0);
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    if (a[i].id === b[i].id) continue;
    return compareSiblings(a[i], b[i]);
  }
  return a.length - b.length;
}
